import { Card, Grid } from "@mui/material";
import { Autocomplete, Box, Button, Typography, IPropDetailContainer, useCommonComponentContext, RoleHelpers } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";

import { Mode } from "../../constants/enums";
import { useData, useSelectData, useSubmit } from "../../hooks/messageTemplateCase/edit";
import useTenantCodenames from "../../hooks/messageTemplate/useTenantCodenames";
import useServiceCodenames from "../../hooks/messageTemplate/useServiceCodenames";
import { IMessageTemplateCaseUseDataProps } from "../../hooks/messageTemplateCase/edit/useData";

export interface MessageTemplateCaseEditContainerProps extends IPropDetailContainer {
    detailInitialData?: IMessageTemplateCaseUseDataProps["initialData"];
}

const MessageTemplateCaseEditContainer = ({ idDetail, mode, onGoBack, onChangeMode, detailInitialData }: MessageTemplateCaseEditContainerProps) => {
    const Strings = useStrings();
    const { userInfo, tenantCode: commonTenantCode, serviceCode: circleServiceCode } = useCommonComponentContext();
    const isServiceAdmin = RoleHelpers.isServiceAdmin(userInfo.userProfile.roleType);
    const tenantCode = isServiceAdmin && detailInitialData?.tenantCode ? detailInitialData?.tenantCode : commonTenantCode; 
    const serviceCode = isServiceAdmin && detailInitialData?.serviceCode ? detailInitialData?.serviceCode : circleServiceCode; 

    const { caseCodeList, messageTemplateList, messageTemplateTypeData, getAllCaseCode, getAllMessageTemplate } = useSelectData({
        tenantCode: tenantCode,
        serviceCode: serviceCode,
        circleServiceCode: circleServiceCode,
    });

    const { data, error, isEdited, setIsEdited, getDetail, setError, onChangeValue, organizationId, } = useData({
        initialData: { ...detailInitialData, tenantCode, serviceCode },
        id: idDetail,
        getAllMessageTemplate,
        getAllCaseCode,
    });
    const { tenantCodenames } = useTenantCodenames({ organizationId });
    const { serviceCodenames } = useServiceCodenames({ organizationId });

    const { handleSubmit } = useSubmit({
        mode,
        data,
        onGoBack,
        onChangeMode,
        getDetail,
        setIsEdited,
        setError,
    });

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 3 }}>
                <Button
                    color={"secondary"}
                    onClick={() => {
                        if (isEdited) {
                            Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, onGoBack);
                        } else {
                            onGoBack();
                        }
                    }}
                >
                    {Strings.Common.GO_BACK}
                </Button>
                <Button sx={{ ml: 2 }} color={"dark"} onClick={handleSubmit}>
                    {mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                </Button>
            </Box>
            <Card>
                <Box p={2}>
                    <Box>
                        <Typography variant="h5">{Strings.MESSAGE_TEMPLATE_CASE.BASIC_INFO}</Typography>
                    </Box>
                    <Grid container spacing={3} p={2}>
                        {isServiceAdmin && <Grid item xs={12} md={6}>
                            <Autocomplete
                                required
                                isSelectedBox
                                mode={mode}
                                data={tenantCodenames || []}
                                defaultValue={data?.tenantCode}
                                label={Strings.MESSAGE_TEMPLATE.TENANT}
                                errorMessage={error?.tenantCode || ""}
                                placeholder={Strings.MESSAGE_TEMPLATE.SELECT_TENANT}
                                onChange={(value) => onChangeValue(value, "tenantCode")}
                            />
                        </Grid>}
                        {isServiceAdmin && <Grid item xs={12} md={6}>
                            <Autocomplete
                                isSelectedBox
                                required
                                mode={mode}
                                data={serviceCodenames || []}
                                defaultValue={data?.serviceCode}
                                label={Strings.MESSAGE_TEMPLATE.SERVICE}
                                errorMessage={error?.tenantCode || ""}
                                placeholder={Strings.MESSAGE_TEMPLATE.SELECT_SERVICE}
                                onChange={(value) => onChangeValue(value, "serviceCode")}
                            />
                        </Grid>}

                        <Grid item xs={12} md={12}>
                            <Autocomplete
                                required
                                mode={mode}
                                defaultValue={data?.templateType}
                                data={messageTemplateTypeData || []}
                                errorMessage={error?.templateType}
                                label={Strings.MESSAGE_TEMPLATE_CASE.TYPE}
                                placeholder={Strings.MESSAGE_TEMPLATE_CASE.SELECT_TYPE}
                                onChange={(value) => onChangeValue(value, "templateType")}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Autocomplete
                                required
                                mode={mode}
                                data={messageTemplateList || []}
                                defaultValue={data?.templateCode}
                                errorMessage={error?.templateCode}
                                label={Strings.MESSAGE_TEMPLATE_CASE.MESSAGE_TEMPLATE}
                                placeholder={Strings.MESSAGE_TEMPLATE_CASE.SELECT_MESSAGE_TEMPLATE}
                                onChange={(value) => onChangeValue(value, "templateCode")}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Autocomplete
                                required
                                mode={mode}
                                data={caseCodeList || []}
                                defaultValue={data?.caseCode}
                                errorMessage={error?.caseCode}
                                label={Strings.MESSAGE_TEMPLATE_CASE.CASE}
                                placeholder={Strings.MESSAGE_TEMPLATE_CASE.SELECT_CASE}
                                onChange={(value) => onChangeValue(value, "caseCode")}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </Box>
    );
};

export default MessageTemplateCaseEditContainer;
